/**
 * Splits the volume of a cancelled auction across the given number of upcoming auctions so that
 * every share is a multiple of the configured step.
 *
 * The volume is divided equally and each share rounded down to the step; whatever is left over is
 * added to the last auction. For a volume of 21 000 spread over four auctions in steps of 500 this
 * yields 5 000, 5 000, 5 000, 6 000.
 *
 * The residual can only be placed when it is itself a multiple of the step, which is the case
 * exactly when the distributed volume is a multiple of the step. Auction volumes are required to be
 * multiples of the step, so a volume that is not means the data is inconsistent and no distribution
 * can be proposed.
 *
 * A volume of zero is valid and yields zero for every auction: an auctioneer may legitimately hold
 * no volume on an auction, even though an auction itself is never distributed with a zero volume.
 *
 * @param {number} volume volume of the cancelled auction, or of one of its auctioneer lines
 * @param {number} count number of auctions to distribute to, in ascending order of opening time
 * @param {number} step the volume of every share has to be a multiple of this value
 * @returns {number[]|null} the volume per auction, or null when the residual cannot be placed
 */
export const distributeInSteps = (volume, count, step) => {
    // A volume of zero is only ever reached through an auctioneer line: an auction is refused
    // outright when its quantity is not a multiple of the step, and Market Operations confirmed a
    // cancelled auction can never carry a quantity of zero, so the auction-level caller cannot
    // reach this guard with zero.
    if (count <= 0 || step <= 0 || volume < 0) {
        return null;
    }
    const base = Math.floor(volume / count / step) * step;
    const lastVolume = base + (volume - base * count);
    if (lastVolume % step !== 0) {
        return null;
    }
    return Array.from({ length: count }, (_, index) => (index === count - 1 ? lastVolume : base));
};

/**
 * Key identifying one auctioneer volume line of one target auction. An auctioneer may hold volume
 * in more than one submarket of the same auction, so the submarket is part of the identity.
 *
 * @param {number|string} auctionId the target auction
 * @param {string} memberId the auctioneer member
 * @param {string} submarketCode the submarket
 * @returns {string} the key
 */
export const auctioneerLineKey = (auctionId, memberId, submarketCode) => `${auctionId}|${memberId}|${submarketCode}`;

/**
 * Splits every auctioneer volume line of a cancelled auction individually across the given number
 * of upcoming auctions, so that the volume of an auctioneer is always redistributed to the very
 * same auctioneer.
 *
 * Each line is split by {@link distributeInSteps}, so each share is a multiple of the step and the
 * remainder of a line lands on the last auction. The volume proposed for an auction is the sum of
 * its lines, which is therefore a multiple of the step as well, and the proposed total matches the
 * cancelled volume exactly.
 *
 * Splitting the auction volume first and dividing it among the auctioneers afterwards cannot
 * achieve this: the per-auctioneer totals would then depend on the rounding inside each auction and
 * would no longer add up to the volume the auctioneer held on the cancelled auction.
 *
 * @param {number[]} lineVolumes volume of each auctioneer line of the cancelled auction
 * @param {number} count number of auctions to distribute to, in ascending order of opening time
 * @param {number} step the volume of every share has to be a multiple of this value
 * @returns {{lineShares: number[][], auctionTotals: number[]}|null} the volume per line and per
 *          auction, or null when any line cannot be distributed in multiples of the step
 */
export const distributeAuctioneerLines = (lineVolumes, count, step) => {
    if (!Array.isArray(lineVolumes) || lineVolumes.length === 0) {
        return null;
    }
    const lineShares = [];
    for (const lineVolume of lineVolumes) {
        const shares = distributeInSteps(lineVolume, count, step);
        if (shares === null) {
            return null;
        }
        lineShares.push(shares);
    }
    const auctionTotals = Array.from(
        { length: count },
        (_, auctionIndex) => lineShares.reduce((sum, shares) => sum + shares[auctionIndex], 0),
    );
    return { lineShares, auctionTotals };
};

/**
 * Key identifying one auctioneer volume line of an auction, independent of the auction it belongs
 * to, so the same line can be recognised on the cancelled auction and on a target auction.
 *
 * @param {object} item an auctioneer data item
 * @returns {string} the key
 */
export const auctioneerItemKey = (item) => `${item.auctioneer.memberId}|${item.submarket.code}`;

/**
 * Sorts auctioneer volume lines by auctioneer and submarket, so every auction presents its break
 * down in the same order and the lines of one auctioneer read as a group.
 *
 * @param {object[]} items auctioneer data items
 * @returns {object[]} a sorted copy
 */
export const sortAuctioneerItems = (items) => [...items].sort((first, second) => (
    first.auctioneer.memberId.localeCompare(second.auctioneer.memberId)
        || first.submarket.code.localeCompare(second.submarket.code)
));

/**
 * The auctioneer lines shown for a target auction: its own lines, plus a line starting at zero for
 * every auctioneer that holds volume on the cancelled auction but is not represented on the target
 * yet, so that auctioneer can still receive its share.
 *
 * @param {object[]} [targetItems] auctioneer data items of the target auction
 * @param {object[]} cancelledItems auctioneer data items of the cancelled auction
 * @returns {object[]} the merged lines, sorted by auctioneer and submarket
 */
export const mergeAuctioneerLines = (targetItems, cancelledItems) => {
    const linesByKey = new Map();
    (targetItems ?? []).forEach((item) => linesByKey.set(auctioneerItemKey(item), item));
    cancelledItems.forEach((item) => {
        const key = auctioneerItemKey(item);
        if (!linesByKey.has(key)) {
            linesByKey.set(key, { auctioneer: item.auctioneer, submarket: item.submarket, quantity: 0 });
        }
    });
    return sortAuctioneerItems([...linesByKey.values()]);
};

/**
 * Reads a volume typed into a field. An empty or absent field has no volume at all, which is
 * distinct from a volume of zero, so it yields NaN rather than 0.
 *
 * @param {string|number} raw the raw field value
 * @returns {number} the volume, or NaN when the field cannot be read as one
 */
export const parseVolume = (raw) => {
    if (raw === undefined || raw === null || String(raw).trim() === '') {
        return Number.NaN;
    }
    return Number(raw);
};

/**
 * Totals, per auctioneer line, the volume distributed to it across all selected auctions. The
 * volume an auctioneer holds on the cancelled auction has to be redistributed to that very same
 * auctioneer in full, so this total is what the allocation rule is checked against and what the
 * 'Allocated Volume' column shows.
 *
 * A line whose field cannot be read yet is reported as incomplete rather than as a total of its
 * own: the field itself already says what is wrong with it, and an allocation built from a
 * half-typed number would accuse the user of a mismatch they are in the middle of fixing.
 *
 * @param {Array<{itemKey: string, rawVolume: string|number}>} entries one entry per auctioneer line
 *        of every selected auction, keyed by {@link auctioneerItemKey}
 * @returns {Object<string, {allocated: number, complete: boolean}>} the total per auctioneer line
 */
export const allocatedVolumesOf = (entries) => {
    const totals = {};
    entries.forEach(({ itemKey, rawVolume }) => {
        const volume = parseVolume(rawVolume);
        const { allocated, complete } = totals[itemKey] ?? { allocated: 0, complete: true };
        totals[itemKey] = Number.isFinite(volume)
            ? { allocated: allocated + volume, complete }
            : { allocated, complete: false };
    });
    return totals;
};

/**
 * The volume an auction or an auctioneer line ends up with: what it holds today plus what is being
 * distributed to it. A field that cannot be read yet contributes nothing, so the total shown is the
 * original volume until a volume is entered.
 *
 * @param {number} originalVolume the volume held before the distribution
 * @param {string|number} rawDistributedVolume the raw value of the distributed volume field
 * @returns {number} the resulting total volume
 */
export const totalVolumeOf = (originalVolume, rawDistributedVolume) => {
    const distributed = parseVolume(rawDistributedVolume);
    return originalVolume + (Number.isFinite(distributed) ? distributed : 0);
};
